import { Link } from 'react-router-dom';
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/feature/PageHero';

export default function ChatGPTAdsPage() {
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistData, setWaitlistData] = useState({ name: '', email: '', business: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleWaitlistSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setShowWaitlist(false);
      setIsSubmitted(false);
      setWaitlistData({ name: '', email: '', business: '' });
    }, 3000);
  };

  const readinessChecklist = [
    { item: 'Brand entity clarity across the web', status: 'critical' },
    { item: 'Conversational messaging framework', status: 'critical' },
    { item: 'Landing pages optimized for AI-driven traffic', status: 'critical' },
    { item: 'Clear value propositions in answer-first format', status: 'important' },
    { item: 'Schema markup for services and offerings', status: 'important' },
    { item: 'Fast, mobile-optimized user experience', status: 'important' },
    { item: 'Tracking infrastructure for AI referral traffic', status: 'recommended' },
    { item: 'Budget allocation and bidding strategy', status: 'recommended' }
  ];

  const aiPlatforms = [
    { name: 'ChatGPT', icon: 'ri-chat-3-line', color: 'bg-navy-800' },
    { name: 'Perplexity', icon: 'ri-search-eye-line', color: 'bg-navy-800' },
    { name: 'Google SGE', icon: 'ri-google-line', color: 'bg-navy-800' },
    { name: 'Bing Copilot', icon: 'ri-microsoft-line', color: 'bg-navy-800' },
    { name: 'Claude', icon: 'ri-robot-2-line', color: 'bg-navy-800' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      {/* Hero */}
      <PageHero
        eyebrow="Emerging Opportunity"
        title="AI Conversational Ads Readiness Strategy"
        subtitle="Position your Florida business for the next wave of digital advertising across ChatGPT, Perplexity, Google SGE, and other AI platforms."
      >
        <Link to="/audit" className="fdme-cta inline-flex items-center gap-2 px-7 py-3.5 text-base">
          Get Readiness Assessment <i className="ri-arrow-right-up-line"></i>
        </Link>
        <button
          onClick={() => setShowWaitlist(true)}
          className="glass-strong inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-base text-slate-800 font-body font-semibold"
        >
          <i className="ri-add-line text-teal-500"></i> Join Testing Waitlist
        </button>
      </PageHero>

      {/* AI Platforms */}
      <section className="px-6 lg:px-12 py-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-display text-navy-900 mb-4">
              Prepare for Multiple AI Platforms
            </h2>
            <p className="text-lg text-navy-600">
              Conversational ads are emerging across the AI ecosystem
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {aiPlatforms.map((platform, index) => (
              <div key={index} className="bg-navy-50 rounded-xl p-6 text-center hover:shadow-lg transition-all">
                <div className={`w-14 h-14 ${platform.color} rounded-lg flex items-center justify-center mx-auto mb-3`}>
                  <i className={`${platform.icon} text-2xl text-orange-400`}></i>
                </div>
                <h3 className="text-sm font-bold text-navy-900">{platform.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Are Conversational AI Ads */}
      <section className="px-6 lg:px-12 py-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-display text-navy-900 mb-6">
              What Are Conversational AI Ads?
            </h2>
            <p className="text-xl text-navy-600 max-w-3xl mx-auto">
              A fundamentally new advertising format designed for AI-powered conversations
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-navy-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-50 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-chat-quote-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold font-display text-navy-900 mb-3">
                Post-Value Placement
              </h3>
              <p className="text-navy-700 leading-relaxed">
                Ads appear after AI provides helpful information—not before. This creates a natural, non-disruptive experience that users actually appreciate.
              </p>
            </div>

            <div className="bg-navy-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-50 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-focus-3-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold font-display text-navy-900 mb-3">
                Context Relevance
              </h3>
              <p className="text-navy-700 leading-relaxed">
                Your ad matches the exact conversation context. If someone asks about HVAC repair in Tampa, your Tampa HVAC business appears—perfectly timed.
              </p>
            </div>

            <div className="bg-navy-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-50 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-heart-3-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold font-display text-navy-900 mb-3">
                Helpful Tone
              </h3>
              <p className="text-navy-700 leading-relaxed">
                Conversational ads feel like recommendations, not interruptions. They extend the helpful nature of the AI conversation itself.
              </p>
            </div>
          </div>

          {/* Benchmark Note */}
          <div className="bg-navy-50 border-l-4 border-teal-500 p-8 rounded-lg">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-bar-chart-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="text-lg font-bold text-navy-900 mb-2">Early Performance Indicators</h4>
                <p className="text-navy-700 leading-relaxed mb-3">
                  Conversational ads have shown higher engagement in similar AI environments. Microsoft Copilot reported significantly higher CTR compared to traditional search ads, suggesting strong early potential for this format across platforms.
                </p>
                <p className="text-sm text-navy-600 italic">
                  Note: These benchmarks are based on early AI advertising research. Actual performance will vary as platforms evolve and expand their ad offerings.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Readiness Checklist */}
      <section className="px-6 lg:px-12 py-20 bg-navy-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-display text-navy-900 mb-6">
              ChatGPT Ads Readiness Checklist
            </h2>
            <p className="text-xl text-navy-600">
              Are you prepared to capitalize when placements expand?
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="space-y-4">
              {readinessChecklist.map((item, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-navy-50 rounded-lg">
                  <div className="flex-shrink-0 mt-1">
                    {item.status === 'critical' && (
                      <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                        <i className="ri-alert-line text-sm text-white"></i>
                      </div>
                    )}
                    {item.status === 'important' && (
                      <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                        <i className="ri-error-warning-line text-sm text-white"></i>
                      </div>
                    )}
                    {item.status === 'recommended' && (
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                        <i className="ri-information-line text-sm text-white"></i>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-navy-900 font-medium">{item.item}</p>
                    <p className="text-sm text-navy-600 mt-1 capitalize">{item.status} priority</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/audit"
                className="btn-primary px-8 py-4 text-lg whitespace-nowrap cursor-pointer"
              >
                Get Your Readiness Score
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="px-6 lg:px-12 py-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-display text-navy-900 mb-6">
              Our ChatGPT Ads Readiness Approach
            </h2>
            <p className="text-xl text-navy-600">
              We prepare your brand for success as conversational ad placements roll out
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-navy-50 border border-navy-100 rounded-xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0 text-navy-900 font-bold">
                  1
                </div>
                <h3 className="text-xl font-bold font-display text-navy-900">
                  Readiness Assessment & Gap Analysis
                </h3>
              </div>
              <p className="text-navy-700 leading-relaxed">
                We assess your current digital presence against conversational ad requirements and identify exactly what needs improvement.
              </p>
            </div>

            <div className="bg-navy-50 border border-navy-100 rounded-xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0 text-navy-900 font-bold">
                  2
                </div>
                <h3 className="text-xl font-bold font-display text-navy-900">
                  Conversational Messaging Strategy
                </h3>
              </div>
              <p className="text-navy-700 leading-relaxed">
                We develop messaging that fits naturally into AI conversations—helpful, relevant, and perfectly timed.
              </p>
            </div>

            <div className="bg-navy-50 border border-navy-100 rounded-xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0 text-navy-900 font-bold">
                  3
                </div>
                <h3 className="text-xl font-bold font-display text-navy-900">
                  Landing Experience Optimization
                </h3>
              </div>
              <p className="text-navy-700 leading-relaxed">
                We ensure your landing pages match conversation intent and convert AI-driven traffic at the highest possible rate.
              </p>
            </div>

            <div className="bg-navy-50 border border-navy-100 rounded-xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0 text-navy-900 font-bold">
                  4
                </div>
                <h3 className="text-xl font-bold font-display text-navy-900">
                  Measurement & Tracking Setup
                </h3>
              </div>
              <p className="text-navy-700 leading-relaxed">
                We implement tracking infrastructure so you can measure performance and optimize from day one.
              </p>
            </div>

            <div className="bg-navy-50 border border-navy-100 rounded-xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0 text-navy-900 font-bold">
                  5
                </div>
                <h3 className="text-xl font-bold font-display text-navy-900">
                  Early Test Participation Support
                </h3>
              </div>
              <p className="text-navy-700 leading-relaxed">
                When testing opportunities become available, we help you apply, set up campaigns, and optimize for maximum impact.
              </p>
            </div>

            <div className="bg-navy-50 border border-navy-100 rounded-xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0 text-navy-900 font-bold">
                  6
                </div>
                <h3 className="text-xl font-bold font-display text-navy-900">
                  Ongoing Optimization
                </h3>
              </div>
              <p className="text-navy-700 leading-relaxed">
                Continuous testing, refinement, and scaling as the platform evolves and your results improve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Accuracy Disclaimer */}
      <section className="px-6 lg:px-12 py-12 bg-navy-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border-l-4 border-teal-500 p-6 rounded-lg">
            <div className="flex items-start gap-4">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                <i className="ri-information-line text-xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-navy-900 mb-2">Important Disclosure</h4>
                <p className="text-sm text-navy-700 leading-relaxed mb-3">
                  Multiple AI platforms including ChatGPT, Perplexity, Google SGE, and Bing Copilot are testing or planning conversational ad formats. Availability, formats, and eligibility vary by platform and may evolve as testing expands.
                </p>
                <p className="text-sm text-navy-700 leading-relaxed">
                  Our service focuses on preparing your brand so you're competitive and ready as new AI ad placements roll out across multiple platforms. We cannot guarantee placement or control AI responses, but we optimize your readiness for when opportunities become available.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 lg:px-12 py-16 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-6">
            Don't Wait Until Your Competitors Are Already Winning
          </h2>
          <p className="text-xl text-navy-100 mb-8">
            Get your free readiness Assessment and position your Florida business for the AI advertising era.
          </p>
          <Link
            to="/audit"
            className="btn-primary inline-block px-10 py-4 text-lg whitespace-nowrap cursor-pointer"
          >
            Get Free Readiness Assessment
          </Link>
        </div>
      </section>

      {/* Waitlist Modal */}
      {showWaitlist && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            {!isSubmitted ? (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold font-display text-navy-900">
                    Join Testing Waitlist
                  </h3>
                  <button
                    onClick={() => setShowWaitlist(false)}
                    className="w-8 h-8 flex items-center justify-center text-navy-400 hover:text-navy-600 cursor-pointer"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>
                <p className="text-navy-600 mb-6">
                  Be notified when ChatGPT ad testing opportunities become available for Florida businesses.
                </p>
                <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-navy-900 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      required
                      value={waitlistData.name}
                      onChange={(e) => setWaitlistData({ ...waitlistData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-navy-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy-900 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={waitlistData.email}
                      onChange={(e) => setWaitlistData({ ...waitlistData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-navy-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy-900 mb-2">
                      Business Name
                    </label>
                    <input
                      type="text"
                      required
                      value={waitlistData.business}
                      onChange={(e) => setWaitlistData({ ...waitlistData, business: e.target.value })}
                      className="w-full px-4 py-3 border border-navy-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn-primary w-full px-6 py-3 whitespace-nowrap cursor-pointer"
                  >
                    Join Waitlist
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-navy-900"></i>
                </div>
                <h3 className="text-2xl font-bold text-navy-900 mb-2">You're on the list!</h3>
                <p className="text-navy-600">We'll notify you when opportunities become available.</p>
              </div>
            )}
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
